import { computed, ref, watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import type {
  DonorFinancialYear,
  DonorDonation,
} from '@/types/donorFinancialYear'
import type { GivingRecordRow } from '@/types/givingRecord'
import { getCurrentUkTaxYear } from '@/helpers/ukTaxYear'
import {
  collectGivingRecordPeriodKeys,
  formatPeriodShortLabel,
  getUkTaxYearPeriodKeyForDate,
  isManualDonationSource,
  parseGivingRecordYearQuery,
} from '@/helpers/givingRecord'
import {
  donationSourceCategory,
  donationSourceLabel,
} from '@/helpers/donationSource'
import { isGiftAidedDonation } from '@/helpers/claimTaxBack/givenTotal'
import { givingRecordRowToDonorDonation } from '@/helpers/givingRecordDonation'

export type GivingRecordTab = 'all' | 'swiftaid' | 'manual'

function mapDonationToRow(
  donation: DonorDonation,
  periodKey?: string
): GivingRecordRow {
  const manual = isManualDonationSource(donation.source)
  const label = donationSourceLabel(donation)
  return {
    id: donation.id,
    charity: donation.charity,
    time: donation.time,
    amountPence: donation.amount,
    giftAidPence: donation.giftaid,
    giftAidDeclared: donation.giftAidDeclared,
    source: manual ? 'manual' : 'swiftaid',
    sourceLabel: manual ? 'manual' : label,
    sourceCategory: donationSourceCategory(donation),
    notes: donation.notes,
    receipts: donation.receipts,
    periodKey: periodKey ?? getUkTaxYearPeriodKeyForDate(donation.time),
    charityLogoUrl: donation.charityLogoUrl,
    charityBrandColor: donation.charityBrandColor,
    recurringSeriesId: donation.recurringSeriesId,
    scheduledOccurrenceDate: donation.scheduledOccurrenceDate,
    individuallyEdited: donation.individuallyEdited,
  }
}

function flattenYears(years: DonorFinancialYear[]): GivingRecordRow[] {
  const rows: GivingRecordRow[] = []
  for (const year of years) {
    for (const donation of year.donations) {
      rows.push(mapDonationToRow(donation, year.period))
    }
  }
  return reverse(sortBy(rows, (r) => r.time))
}

/** Same Gift Aid rule as the claim guide, so the two screens stay in step. */
function isGiftAidedRow(row: GivingRecordRow): boolean {
  return isGiftAidedDonation(givingRecordRowToDonorDonation(row))
}

/**
 * Paid giving record: flattened donations, year filter, tabs, and summary metrics.
 */
export function useGivingRecord(
  sortedYears: Ref<DonorFinancialYear[]>,
  manualDonations: Ref<DonorDonation[]>
) {
  const route = useRoute()
  const yearFilter = ref<string>(
    parseGivingRecordYearQuery(route.query.year) ?? 'all'
  )

  // Deep links (e.g. Claim tax back "View or record more donations") pass ?year=2025-2026.
  watch(
    () => route.query.year,
    (year) => {
      const next = parseGivingRecordYearQuery(year)
      if (next) yearFilter.value = next
    }
  )
  const activeTab = ref<GivingRecordTab>('all')

  const allRows = computed(() => {
    const apiRows = flattenYears(sortedYears.value)
    const manualRows = manualDonations.value.map((d) => mapDonationToRow(d))
    const merged = [...manualRows, ...apiRows]
    return reverse(sortBy(merged, (r) => r.time))
  })

  const periodFilterOptions = computed(() => {
    const periods = collectGivingRecordPeriodKeys({
      apiPeriods: sortedYears.value.map((year) => year.period),
      rowPeriodKeys: allRows.value.map((row) => row.periodKey),
      selectedPeriod: yearFilter.value,
    })
    return periods.map((period) => ({
      value: period,
      label: formatPeriodShortLabel(period),
    }))
  })

  const filteredRows = computed(() => {
    if (yearFilter.value === 'all') return allRows.value
    return allRows.value.filter((r) => r.periodKey === yearFilter.value)
  })

  const tabRows = computed(() => {
    if (activeTab.value === 'swiftaid') {
      return filteredRows.value.filter((r) => r.source === 'swiftaid')
    }
    if (activeTab.value === 'manual') {
      return filteredRows.value.filter((r) => r.source === 'manual')
    }
    return filteredRows.value
  })

  const metricsPeriodLabel = computed(() => {
    if (yearFilter.value === 'all') return 'all'
    if (yearFilter.value === getCurrentUkTaxYear().periodKey) return 'current'
    return formatPeriodShortLabel(yearFilter.value)
  })

  const metrics = computed(() => {
    const rows = filteredRows.value
    const giftAidedRows = rows.filter(isGiftAidedRow)
    const charityNames = new Set(rows.map((r) => r.charity.toLowerCase()))
    const totalGivingPence = rows.reduce((sum, r) => sum + r.amountPence, 0)
    const giftAidedGivingPence = giftAidedRows.reduce(
      (sum, r) => sum + r.amountPence,
      0
    )
    return {
      totalGivingPence,
      giftAidedGivingPence,
      // Only when some gifts in this view had no Gift Aid, so the claim
      // total can differ from Total giving without adding a fourth card.
      showGiftAidedGiving: giftAidedGivingPence !== totalGivingPence,
      totalGiftAidPence: rows.reduce((sum, r) => sum + r.giftAidPence, 0),
      charitiesCount: charityNames.size,
    }
  })

  const setTab = (tab: GivingRecordTab) => {
    activeTab.value = tab
  }

  return {
    yearFilter,
    periodFilterOptions,
    activeTab,
    tabRows,
    metricsPeriodLabel,
    metrics,
    setTab,
  }
}
